import { useEffect, useRef, useState } from 'react';

const officeImages = [
  {
    src: '/images/sto1.jpg',
    alt: '신가영 법률사무소 사무실 전경',
    caption: '사무실 전경',
  },
  {
    src: '/images/sto2.jpg',
    alt: '신가영 법률사무소 서재 및 수상 경력',
    caption: '서재 및 수상 경력',
  },
  {
    src: '/images/sto3.jpg',
    alt: '신가영 법률사무소 사무실 입구',
    caption: '사무실 입구',
  },
  {
    src: '/images/sto4.jpg',
    alt: '신가영 법률사무소 사무실 모습',
    caption: '사무실 모습',
  },
  {
    src: '/images/sto5.jpg',
    alt: '신가영 법률사무소 내부 전경',
    caption: '내부 전경',
  },
  {
    src: '/images/sto6.jpg',
    alt: '신가영 법률사무소 상담 공간',
    caption: '상담 공간',
  },
  {
    src: '/images/sto7.jpg',
    alt: '신가영 법률사무소 사무실 풍경',
    caption: '사무실 풍경',
  },
  {
    src: '/images/sto8.jpg',
    alt: '신가영 법률사무소 업무 공간',
    caption: '업무 공간',
  },
  {
    src: '/images/sto9.jpg',
    alt: '신가영 법률사무소 사무실 내부',
    caption: '사무실 내부',
  },
  {
    src: '/images/sto10.jpg',
    alt: '신가영 법률사무소 사무실 전경',
    caption: '사무실 전경',
  },
  {
    src: '/images/sto11.jpg',
    alt: '신가영 법률사무소 사무실 모습',
    caption: '사무실 모습',
  },
  {
    src: '/images/sto12.jpg',
    alt: '신가영 법률사무소 사무실 풍경',
    caption: '사무실 풍경',
  },
  {
    src: '/images/sto13.jpg',
    alt: '신가영 법률사무소 사무실 전경',
    caption: '사무실 전경',
  },
];

export default function OfficeGallery() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!lightboxOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxOpen(false);
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    window.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [lightboxOpen, currentIndex]);

  const openLightbox = (index: number) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
  };

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % officeImages.length);
  };

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + officeImages.length) % officeImages.length);
  };

  return (
    <section ref={sectionRef} className="relative w-full py-10 md:py-16 bg-background-50">
      <div className="w-full px-4 md:px-8 lg:px-12 max-w-6xl mx-auto">
        <div className="flex justify-center mb-6 md:mb-8">
          <img
            src="/images/logo_t.png"
            alt="신가영 법률사무소 로고"
            className="h-10 md:h-14 w-auto object-contain"
          />
        </div>

        <div
          className={`transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="relative group cursor-pointer max-w-3xl mx-auto" onClick={() => openLightbox(0)}>
            <div className="relative rounded-2xl overflow-hidden shadow-lg">
              <img
                src={officeImages[0].src}
                alt={officeImages[0].alt}
                className="w-full h-[260px] md:h-[420px] object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <span className="absolute top-4 right-4 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-full">
                <i className="ri-zoom-in-line text-white text-xl md:text-2xl" />
              </span>
            </div>

            {/* 오버랩 썸네일 */}
            <div className="flex justify-center mt-4 md:mt-5">
              <div className="flex items-center -space-x-3 md:-space-x-4">
                {officeImages.slice(1, 4).map((img, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openLightbox(index + 1);
                    }}
                    className="relative w-12 h-12 md:w-16 md:h-16 rounded-xl overflow-hidden border-2 border-background-50 shadow-md hover:scale-110 hover:z-10 transition-transform duration-300 cursor-pointer"
                  >
                    <img
                      src={img.src}
                      alt={img.alt}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    openLightbox(4);
                  }}
                  className="relative w-12 h-12 md:w-16 md:h-16 rounded-xl overflow-hidden border-2 border-background-50 shadow-md hover:scale-110 hover:z-10 transition-transform duration-300 cursor-pointer bg-background-200"
                >
                  <img
                    src={officeImages[4].src}
                    alt={officeImages[4].alt}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <span className="text-white text-xs md:text-sm font-bold">+10</span>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4 md:p-8"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            type="button"
            aria-label="닫기"
            className="absolute top-4 right-4 md:top-6 md:right-6 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            onClick={() => setLightboxOpen(false)}
          >
            <i className="ri-close-line text-2xl md:text-3xl" />
          </button>

          <button
            type="button"
            aria-label="이전 이미지"
            className="absolute left-3 md:left-6 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              prevImage();
            }}
          >
            <i className="ri-arrow-left-line text-2xl md:text-3xl" />
          </button>

          <div
            className="max-w-5xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={officeImages[currentIndex].src}
              alt={officeImages[currentIndex].alt}
              className="w-full max-h-[75vh] object-contain rounded-lg"
            />
            <div className="mt-4 flex items-center justify-between text-white">
              <p className="text-sm md:text-base">{officeImages[currentIndex].caption}</p>
              <p className="text-sm text-white/70">
                {currentIndex + 1} / {officeImages.length}
              </p>
            </div>
          </div>

          <button
            type="button"
            aria-label="다음 이미지"
            className="absolute right-3 md:right-6 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              nextImage();
            }}
          >
            <i className="ri-arrow-right-line text-2xl md:text-3xl" />
          </button>
        </div>
      )}
    </section>
  );
}